import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getRefreshToken } from "../utils/auth";
import { getUserProfile } from "../utils/user";

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;
const initialState = {
  isAuthenticated: getRefreshToken() ? true : false,
  user: getUserProfile(),
  loading: false,
  error: null,
};

export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password }) => {
    try {
      const response = await axios.post(
        `${API_ENDPOINT}/auth/login`,
        { username, password },
        { withCredentials: true }
      );

      if (response.status === 200) {
        localStorage.setItem("refresh_token", response.data.refreshToken); // Save refresh token
        localStorage.setItem(
          "user_profile",
          JSON.stringify(response.data.user)
        ); // Save user profile
        return {
          user: response.data.user,
        };
      }

      throw new Error(response.statusText);
    } catch (error) {
      throw error;
    }
  }
);

// Async action to refresh the access token
export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    const refreshToken = getRefreshToken();
    console.log("refresh_token", refreshToken);
    if (!refreshToken) {
      return rejectWithValue("No refresh token found");
    }

    try {
      const response = await axios.post(
        `${API_ENDPOINT}/auth/refresh`,
        { refreshToken },
        { withCredentials: true } // Send the cookie with credentials
      );

      if (response.status === 200) {
        localStorage.setItem("refresh_token", response.data.refreshToken); // Update refresh token
        return { accessToken: response.data.accessToken };
      }

      if (response.status === 403) {
        return rejectWithValue("Refresh token expired");
      }

      return rejectWithValue("Failed to refresh token");
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to refresh token");
    }
  }
);

// Logout action
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_ENDPOINT}/auth/logout`,
        {},
        { withCredentials: true }
      );

      if (response.status === 200) {
        localStorage.removeItem("refresh_token"); // Remove refresh token from localStorage
        localStorage.removeItem("user_profile"); // Remove user profile from localStorage
        return { success: true };
      }

      return rejectWithValue("Failed to log out");
    } catch (error) {
      return rejectWithValue(error.response?.data || "Logout request failed");
    }
  }
);

// Create slice for auth reducer
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = JSON.stringify(action.payload.user);
        state.loading = false;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Login failed";
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.error = null;
        state.isAuthenticated = true;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload || "Failed to refresh token";
      })
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.error = action.payload || "Logout failed";
      });
  },
});

// Export reducer
export default authSlice.reducer;
